/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import classNames from 'classnames';

import initBem from 'js/lib/bem';
import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';
import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

import { Typography2, VisuallyHidden } from '@coursera/cds-core';

import CopyDeterrenceNotification from 'bundles/academic-integrity/components/CopyDeterrenceNotification';
import { isPreventCopyingQuestionPromptEnabled } from 'bundles/academic-integrity/lib/epics';
import { CMLOrHTML } from 'bundles/cml';
import type { FormPart as FormPartType } from 'bundles/compound-assessments/components/api/AssignmentPresentation';
import type {
  FormElement,
  PromptRequiringResponseElement,
  TextBlockElement,
} from 'bundles/compound-assessments/components/api/types/CompoundAssessmentsForm';
import AiGradedTag from 'bundles/compound-assessments/components/form-parts/AiGradedTag';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import FormPart from 'bundles/compound-assessments/components/form-parts/FormPart';
import QuestionPoints from 'bundles/compound-assessments/components/form-parts/QuestionPoints';
import ReviewRubricHeader from 'bundles/compound-assessments/components/form-parts/ReviewRubricHeader';
import getFormPartData from 'bundles/compound-assessments/components/form-parts/lib/getFormPartData';
import { getFillableBlankCount } from 'bundles/compound-assessments/components/form-parts/multipleFillableBlanks/utils';
import TextBlock from 'bundles/compound-assessments/components/form-parts/textBlock/TextBlock';
import type { Response as ChangedResponseType } from 'bundles/compound-assessments/components/local-state/changed-response/types';
import { GraderTypes, typeNames } from 'bundles/compound-assessments/constants';
import { QuestionContextProvider } from 'bundles/compound-assessments/contexts/QuestionContext';
import getQuestionGradeFeedbackInfo from 'bundles/compound-assessments/lib/utils/getQuestionGradeFeedbackInfo';
import type {
  QuizQuestionPrompt,
  QuizQuestionResponse,
  SubmissionPartPrompt,
  SubmissionPartResponse,
} from 'bundles/compound-assessments/types/FormParts';
import type { AssignmentRole } from 'bundles/compound-assessments/types/Roles';

import _t from 'i18n!nls/compound-assessments';

/* We have another rc-Question class, so this one is rc-FormPartsQuestion.
  This selector is used in bundles/authoring/content-authoring/components/CMLEditor.jsx
  and shouldn't be removed. */
const bem = initBem('FormPartsQuestion');

const styles = {
  container: css`
    width: 100%;
    margin-top: var(--cds-spacing-600);
    margin-bottom: var(--cds-spacing-600);

    & + & {
      margin-top: var(--cds-spacing-800);
    }

    /* Override legend */
    legend {
      font-size: 14px;
      margin: 0;
      border: none;
    }

    /* Override default 20px font in CUI inputs */
    textarea,
    input {
      font-size: 14px;
      line-height: 24px;
      display: block;
      border-color: var(--cds-color-neutral-primary-weak);
    }

    ::placeholder {
      color: black;
      opacity: 1;
    }
  `,

  grid: css`
    display: grid;
    grid-template-areas:
      'heading-area grading-area'
      'response-area grading-area';
    grid-template-columns: auto 136px;

    @media (max-width: 1024px) {
      grid-template-columns: auto;
      grid-template-areas:
        'heading-area'
        'grading-area'
        'response-area';
    }
  `,
  gridNoQuestion: css`
    grid-template-areas: 'response-area';
    grid-template-columns: 100%;
  `,

  headingArea: css`
    grid-area: heading-area;
    margin-bottom: var(--cds-spacing-150);
  `,
  gradingArea: css`
    grid-area: grading-area;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
    text-align: right;

    @media (max-width: 1024px) {
      flex-direction: row;
      margin-left: 30px;
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  responseArea: css`
    grid-area: response-area;
  `,

  questionRubricFormPart: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  row: css`
    display: flex;
    align-items: baseline;

    & + & {
      margin-top: var(--cds-spacing-150);
    }
  `,
  numberCell: css`
    width: 30px;
  `,
  contentCell: css`
    width: 100%;
  `,
};

type Props = {
  index?: number | null;
  isReadOnly?: boolean | null;
  isReviewPhase?: boolean;
  isDisabled?: boolean | null;
  isAutoSaving?: boolean | null;
  formPart: FormPartType;
  formPartNumber?: number;
  localScopeId?: string | null;
  showValidation?: boolean | null;
  courseId?: string;
  itemId?: string;
  userId?: number;
  teamId?: string;
  hideRubric?: boolean;
  role?: AssignmentRole;
  /** Sets FormPart's isReadOnly prop to true for the answer FormPart and false
   *  for grader rubrics FormPart(s) in grading view.
   *  This is needed because FormPart reads from the ChangedResponse
   *  state for non-readonly children and this prevents updates to the readonly view
   */
  isGraderView?: boolean;
  isGradeHidden?: boolean;
  renderFileUploadForPreview?: boolean; // renders file uploads for file upload submissions in readOnly view
  firstTextBlockFormPartId?: string;
};

/**
 * This component renders any question type.
 */
const Question: React.FC<Props> = ({
  index,
  isReviewPhase,
  formPart,
  formPartNumber,
  /**
   * isReadOnly means that field is not editable and it is read-only. It is used to render
   * submitted or graded assignment in view-only mode.
   */
  isReadOnly,
  /** isDisabled indicates that field is editable but temporarily disabled. */
  isDisabled,
  isAutoSaving,
  localScopeId,
  showValidation,
  courseId,
  itemId,
  userId,
  role,
  hideRubric = false,
  isGraderView,
  isGradeHidden,
  renderFileUploadForPreview,
  firstTextBlockFormPartId,
  teamId,
}) => {
  const track = useRetracked();
  const {
    root: { element, children },
  } = formPart;

  const [showCopyDeterrenceNotification, setCopyDeterrenceNotification] = useState(false);
  if (element.typeName === typeNames.TEXT_BLOCK_ELEMENT) {
    const textBlockElementDefinition = element.definition as TextBlockElement;

    return (
      <TextBlock
        title={textBlockElementDefinition?.title}
        body={textBlockElementDefinition?.body}
        expanded={firstTextBlockFormPartId === formPart.id}
      />
    );
  }

  const formPartData = getFormPartData(element);
  const { id, response, prompt, promptText, score, maxScore, isExtraCreditQuestion } = formPartData;

  let questionNumber = formPartNumber;

  if (!questionNumber && typeof questionNumber !== 'number' && typeof index === 'number') {
    questionNumber = index + 1;
  }

  const questionPrompt =
    'prompt' in formPart.root.element.definition ? formPart.root.element.definition.prompt : undefined;
  const questionPromptValue =
    questionPrompt && 'value' in questionPrompt.definition ? questionPrompt.definition.value : undefined;
  const feedback = getQuestionGradeFeedbackInfo({ prompt: questionPromptValue });
  const showQuestionPrompt = questionPrompt?.typeName !== typeNames.REVIEW_PROMPT;

  const legendId = formPart.id + '-legend';
  const multipleFillableBlanksAriaId = formPart.id + '-multipleFillableBlanks';
  const isMultipleFillableBlanksQuestion = prompt?.question?.type === 'multipleFillableBlanks';
  const fillableBlankCount = getFillableBlankCount(prompt);

  const getRubricFormPartsIsReadOnlyState = (childFormPart: FormElement) => {
    const childFormPartData = getFormPartData(childFormPart);
    const { questionType } = childFormPartData;

    // override readOnly state if this rubric is an inline-feedback used by a grader with the submitter's grade visible
    if (isGraderView) {
      // since isGradeHidden can be undefined, explicitly check for false
      return questionType === 'boxViewDocumentAnnotation' && isGradeHidden === false;
    } else {
      return !!isReadOnly;
    }
  };

  const preventAction = (e: React.SyntheticEvent) => {
    if (!isGraderView && isPreventCopyingQuestionPromptEnabled()) {
      track({
        trackingName: 'prevent_prompt_copy',
        action: e.type,
        trackingData: { userId: user.get().id, partId: id, partNumber: questionNumber },
      });
      e.preventDefault();
      setCopyDeterrenceNotification(true);
    }
  };

  const graderTypeValue =
    questionPrompt?.typeName === typeNames.SUBMISSION_PROMPT
      ? questionPrompt.definition.submissionPartSchema.graderType
      : GraderTypes.Auto;

  const isAiGraded = graderTypeValue === GraderTypes.Ai;

  /**
   * The layout of this component is based on a 3x3 grid where the first and third columns contain
   * optional data and empty divs are used to fill out the grid.  If number or points data is not
   * provided, those column may optionally not render.
   * -------------------------------
   * | number |   prompt  | points |
   * -------------------------------
   * |        |   input   |        |
   * -------------------------------
   * |        | quiz help |        |
   * -------------------------------
   * The reason for this layout is to create an explicit structure based on boxes to support RTL display
   * instead of using CSS negative margins, absolute positioning, or coordinate transforms that can
   * have difficulty being converted for RTL display.
   */
  return (
    <QuestionContextProvider
      isReadOnly={isReadOnly ?? false}
      isDisabled={isDisabled ?? false}
      showValidation={showValidation ?? false}
      isGraderView={isGraderView ?? false}
      responseId={id || ''}
      localScopeId={localScopeId || ''}
      prompt={prompt}
      response={response as ChangedResponseType}
    >
      <div
        className="rc-FormPartsQuestion"
        css={[styles.container, styles.grid, !showQuestionPrompt && styles.gridNoQuestion]}
        aria-labelledby={isMultipleFillableBlanksQuestion ? `${multipleFillableBlanksAriaId} ${legendId}` : legendId}
      >
        {isMultipleFillableBlanksQuestion && (
          <VisuallyHidden id={multipleFillableBlanksAriaId}>
            <FormattedMessage
              message={_t(
                'The following question contains {count} dropdown {count, plural, =1 {response} other {responses}}.'
              )}
              count={fillableBlankCount}
            />
          </VisuallyHidden>
        )}

        {showQuestionPrompt && (
          <div id={legendId} css={styles.headingArea} data-testid="legend" onCut={preventAction} onCopy={preventAction}>
            <div css={styles.row} className={bem('row')}>
              {questionNumber && (
                <div className={bem('numberCell')} css={styles.numberCell}>
                  <Typography2 component="h3" variant="subtitleMedium" aria-hidden>
                    <FormattedNumber value={questionNumber} aria-hidden />.
                  </Typography2>
                  <VisuallyHidden component="span">
                    <FormattedMessage message={_t('Question {questionNumber}')} questionNumber={questionNumber} />
                  </VisuallyHidden>
                </div>
              )}
              <div className={bem('contentCell')} css={styles.contentCell} id={`prompt-${prompt.id}`}>
                <Typography2 variant="bodyPrimary" component="div">
                  <CMLOrHTML
                    value={promptText}
                    display="inline-block"
                    shouldRenderFillableBlanks={isMultipleFillableBlanksQuestion}
                  />
                </Typography2>
              </div>
            </div>
          </div>
        )}

        {(maxScore || isAiGraded) && (
          <div css={styles.gradingArea}>
            {!!maxScore && (
              <QuestionPoints score={score} maxScore={maxScore} isExtraCreditQuestion={isExtraCreditQuestion} />
            )}
            {isAiGraded && <AiGradedTag />}
          </div>
        )}

        <div css={styles.responseArea}>
          {isPreventCopyingQuestionPromptEnabled() && showCopyDeterrenceNotification && (
            <div className={bem('row')} css={styles.row}>
              {questionNumber && <div css={styles.numberCell} />}
              <div className={bem('contentCell')} css={styles.contentCell}>
                <CopyDeterrenceNotification onDismiss={() => setCopyDeterrenceNotification(false)} />
              </div>
            </div>
          )}

          <div css={styles.row} className={classNames(bem('row'), 'pii-hide')} data-testid="form-part">
            {questionNumber && <div css={styles.numberCell} />}
            <div className={bem('contentCell')} css={styles.contentCell}>
              <FormPart
                formPart={element}
                isReadOnly={isGraderView ? true : !!isReadOnly}
                isDisabled={!!isDisabled}
                isAutoSaving={!!isAutoSaving}
                localScopeId={localScopeId}
                showValidation={isReviewPhase ? false : !!showValidation}
                courseId={courseId}
                itemId={itemId}
                userId={userId}
                teamId={teamId}
                role={role}
                ariaLabelledBy={legendId}
                ariaDisabled={!!isReadOnly || !!isDisabled || isGraderView}
                isGraderView={isGraderView}
                renderFileUploadForPreview={renderFileUploadForPreview}
              />
              {children && children.length > 0 && !hideRubric && (
                <div data-testid="questionRubric">
                  <ReviewRubricHeader>
                    <Typography2 component="h3" variant="subtitleMedium">
                      {_t('Grading Rubric')}
                    </Typography2>
                  </ReviewRubricHeader>
                  {children.map(({ element: childFormPart }) => (
                    <div
                      className={bem('questionRubricFormPart')}
                      css={styles.questionRubricFormPart}
                      key={`rubricResponseId-${
                        (childFormPart.definition as PromptRequiringResponseElement).responseId
                      }`}
                    >
                      <FormPart
                        formPart={childFormPart}
                        isReadOnly={getRubricFormPartsIsReadOnlyState(childFormPart)}
                        isDisabled={!!isDisabled}
                        localScopeId={localScopeId}
                        showValidation={!!showValidation}
                        courseId={courseId}
                        itemId={itemId}
                        userId={userId}
                        role={role}
                        isGraderView={isGraderView}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </QuestionContextProvider>
  );
};

type QuizQuestionProps = {
  index: number;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  quizQuestion: {
    prompt: QuizQuestionPrompt | SubmissionPartPrompt;
    response: QuizQuestionResponse | SubmissionPartResponse;
  };
  showValidation?: boolean;
};

export const QuizQuestion: React.FC<QuizQuestionProps> = ({
  index,
  /**
   * isReadOnly means that field is not editable and it is read only. It is used to render
   * submitted or graded assignment in view only mode.
   */
  isReadOnly,
  isDisabled,
  quizQuestion: { prompt, response },
  showValidation,
}) => {
  const formPart = {
    id: prompt.id,
    root: {
      element: {
        definition: {
          prompt: {
            typeName: typeNames.AUTO_GRADABLE_PROMPT,
            definition: {
              value: prompt,
            },
          },
          response,
          responseId: prompt.id,
        },
        typeName: typeNames.PROMPT_REQUIRING_RESPONSE_ELEMENT,
      },
      children: [],
    },
  } as FormPartType;
  return (
    <Question
      {...{
        index,
        formPart,
        isReadOnly: !!isReadOnly,
        isDisabled: !!isDisabled,
        showValidation: !!showValidation,
      }}
    />
  );
};

export default Question;
